import { Path } from 'slate';
import { defineTransform } from '../transform';

import {
  merge,
  pathWasRemoved,
  textOpBounds,
  adjustNodePathForNodeMove,
  adjustPathForNodeSplit,
  adjustPositionForNodeSplit,
  adjustPathForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
} from '../utils';

defineTransform('split_node', 'insert_text', (op1, op2, side) => {
  if (!Path.equals(op1.path, op2.path)) return op1;
  if (op1.position < op2.offset) return op1;
  if (op1.position === op2.offset && side === 'right') return op1;

  return {
    ...op1,
    position: op1.position + op2.text.length,
  };
});

defineTransform('split_node', 'remove_text', (op1, op2, side) => {
  if (!Path.equals(op1.path, op2.path)) return op1;
  if (op1.position < op2.offset) return op1;
  const bounds2 = textOpBounds(op2);
  if (op1.position >= bounds2.start && op1.position < bounds2.end) {
    return {
      ...op1,
      position: op2.offset,
    };
  }
  return {
    ...op1,
    position: op1.position - op2.text.length,
  };
});

defineTransform('split_node', 'split_node', (op1, op2, side) => {
  const tOp = { ...op1 };

  tOp.path = adjustPathForNodeSplit(
    op1.path,
    op1.position,
    op2,
    side === 'left'
  );

  // Splitting a child of the node we want to split, might affect
  // our position (imagine we were splitting at node 4, which is now
  // node 5)
  if (
    Path.isAncestor(op1.path, op2.path) &&
    op2.path.length === op1.path.length + 1
  ) {
    if (op1.position <= op2.path[op1.path.length]) return tOp;
    tOp.position = op1.position + 1;
    return tOp;
  }

  // Same operation, do nothing
  if (Path.equals(op1.path, op2.path) && op1.position === op2.position) return;

  tOp.position = adjustPositionForNodeSplit(
    op1.path,
    op1.position,
    op2,
    side === 'left'
  );
  return tOp;
});

defineTransform('split_node', 'merge_node', (op1, op2, side) => {
  const tOp = { ...op1 };

  tOp.path = adjustPathForNodeMerge(op1.path, op2);

  if (Path.equals(op1.path, op2.path)) {
    // We merged the node we're trying to split, so we need to adjust
    // for the new nodes.
    tOp.position = tOp.position + op2.position;
  }

  // Merging a child of the node we want to split, might affect
  // our position (imagine we were splitting at node 4, which is now
  // node 3)
  if (
    Path.isAncestor(op1.path, op2.path) &&
    op2.path.length === op1.path.length + 1
  ) {
    if (op1.position < op2.path[op1.path.length]) return tOp;
    tOp.position = op1.position - 1;
    return tOp;
  }

  return tOp;
});

defineTransform('split_node', 'move_node', (op1, op2, side) => {
  const tOp = { ...op1 };
  tOp.path = adjustNodePathForNodeMove(op1.path, op2);

  // If we moved one of our child nodes, we need to adjust the
  // position for the now missing child.
  if (
    Path.isAncestor(op1.path, op2.path) &&
    op2.path.length === op1.path.length + 1
  ) {
    if (op1.position > op2.path[op1.path.length]) {
      tOp.position = op1.position - 1;
    }
  }

  if (
    Path.isAncestor(op1.path, op2.newPath) &&
    op2.newPath.length === op1.path.length + 1
  ) {
    if (op1.position > op2.newPath[op1.path.length]) {
      tOp.position = tOp.position + 1;
    }
  }

  return tOp;
});

defineTransform('split_node', 'insert_node', (op1, op2, side) => {
  const tOp = { ...op1 };

  // If a node is being inserted as a child, before our split
  // position, our split position has to take that new node into
  // account.
  if (
    op2.path.length === op1.path.length + 1 &&
    Path.isAncestor(op1.path, op2.path) &&
    op2.path[op2.path.length - 1] <= op1.position
  ) {
    tOp.position += 1;
    return tOp;
  }

  tOp.path = adjustPathForNodeInsert(tOp.path, op2, true);

  return tOp;
});

defineTransform('split_node', 'remove_node', (op1, op2, path) => {
  const tOp = { ...op1 };

  if (pathWasRemoved(op1.path, op2.path)) return;

  if (
    op2.path.length === op1.path.length + 1 &&
    Path.isAncestor(op1.path, op2.path) &&
    op2.path[op2.path.length - 1] < op1.position
  ) {
    tOp.position -= 1;
    return tOp;
  }

  tOp.path = adjustPathForNodeRemove(op1.path, op2, false);

  return tOp;
});

defineTransform('split_node', 'set_node', (op1, op2, side) => {
  if (!Path.equals(op1.path, op2.path)) return op1;

  const tOp = { ...op1 };
  tOp.properties = merge(op1.properties, op2.newProperties);
  return tOp;
});

defineTransform('split_node', 'add_mark', (op1, op2, side) => op1);
defineTransform('split_node', 'remove_mark', (op1, op2, side) => op1);
defineTransform('split_node', 'set_mark', (op1, op2, side) => op1);
defineTransform('split_node', 'add_annotation', (op1, op2, side) => op1);
defineTransform('split_node', 'remove_annotation', (op1, op2, side) => op1);
defineTransform('split_node', 'set_annotation', (op1, op2, side) => op1);
defineTransform('split_node', 'set_selection', (op1, op2, side) => op1);
defineTransform('split_node', 'add_metadata', (op1, op2, side) => op1);
defineTransform('split_node', 'remove_metadata', (op1, op2, side) => op1);
defineTransform('split_node', 'set_metadata', (op1, op2, side) => op1);
